import { useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Skeleton } from "@cieda/djayma-ui";
import {
  ArrowLeft,
  AlertTriangle,
  User,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Truck,
  Calendar,
  Package,
} from "lucide-react";
import { toast } from "sonner";

import AdminLayout from "@/components/admin/AdminLayout";
import { EmptyState, StatusBadge } from "@/components/admin/shared";
import { OrderSummary } from "@/components/admin/orders/OrderSummary";
import { OrderItemsTable } from "@/components/admin/orders/OrderItemsTable";
import { OrderTimeline } from "@/components/admin/orders/OrderTimeline";
import { OrderActions } from "@/components/admin/orders/OrderActions";
import { OrderNotes } from "@/components/admin/orders/OrderNotes";
import { ordersApi } from "@/lib/admin-api/orders";
import type {
  OrderStatus,
  OrderNote,
} from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const orderKeys = {
  detail: (id: string) => ["admin", "orders", id] as const,
  notes: (id: string) => ["admin", "orders", id, "notes"] as const,
};

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function OrderDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-48 w-full rounded-xl" />
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-24 w-full rounded-xl" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();

  // Fetch order detail — surface real errors instead of silently falling back
  // to mock data. Mock fallbacks masked backend failures (auth, 404, transition
  // conflicts) and caused users to click actions on fake "confirmed" orders,
  // producing "Impossible de mettre à jour le statut" toasts.
  const {
    data: orderData,
    isLoading: isOrderLoading,
    isError: isOrderError,
    error: orderError,
  } = useQuery({
    queryKey: orderKeys.detail(id!),
    queryFn: () => ordersApi.get(id!),
    enabled: !!id,
    retry: false,
  });

  const {
    data: notes = [],
    isLoading: isNotesLoading,
  } = useQuery({
    queryKey: orderKeys.notes(id!),
    queryFn: () => ordersApi.getNotes(id!),
    enabled: !!id,
    retry: false,
    meta: { silent: true },
  });

  // Status mutation
  const statusMutation = useMutation({
    mutationFn: (newStatus: OrderStatus) => ordersApi.updateStatus(id!, newStatus),
    onSuccess: () => {
      toast.success("Statut de la commande mis à jour");
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(id!) });
    },
    onError: () => {
      toast.error("Impossible de mettre à jour le statut");
    },
  });

  // Add note mutation
  const noteMutation = useMutation({
    mutationFn: (text: string) => ordersApi.addNote(id!, text),
    onSuccess: (newNote) => {
      toast.success("Note ajoutée");
      queryClient.setQueryData<readonly OrderNote[]>(
        orderKeys.notes(id!),
        (prev) => (prev ? [newNote, ...prev] : [newNote]),
      );
    },
    onError: () => {
      toast.error("Impossible d'ajouter la note");
    },
  });

  const handleStatusChange = useCallback(
    (newStatus: OrderStatus) => {
      statusMutation.mutate(newStatus);
    },
    [statusMutation],
  );

  const handleAddNote = useCallback(
    (text: string) => {
      noteMutation.mutate(text);
    },
    [noteMutation],
  );

  const handlePrintDeliverySlip = useCallback(async () => {
    if (!id) return;
    try {
      // window.open can't attach Authorization headers, which is why opening
      // the PDF URL directly returned "Token d'authentification requis".
      // Fetch the blob through adminFetch (which sets the Bearer token),
      // then open the blob URL in a new tab.
      const blob = await ordersApi.getInvoicePdf(id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Release the object URL after the browser has had time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Impossible de générer la facture",
      );
    }
  }, [id]);

  const handleInitiateRefund = useCallback(() => {
    navigate(`/admin/refunds?order_id=${id}`);
  }, [id, navigate]);

  const handleBack = useCallback(() => {
    navigate("/admin/orders");
  }, [navigate]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Back button + page header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="text-stone-600"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="ml-1">Retour aux commandes</span>
          </Button>
        </div>

        {/* Loading state */}
        {isOrderLoading && <OrderDetailSkeleton />}

        {/* Error state */}
        {isOrderError && !orderData && (
          <EmptyState
            icon={<AlertTriangle className="h-6 w-6" />}
            title="Commande introuvable"
            description={
              orderError instanceof Error
                ? orderError.message
                : "La commande demandée n'existe pas ou n'est plus disponible."
            }
            action={{
              label: "Retour aux commandes",
              onClick: handleBack,
            }}
          />
        )}

        {/* Main content */}
        {orderData && (
          <>
            {/* Order summary */}
            <OrderSummary
              order={orderData.order}
              delivery={orderData.delivery}
            />

            {/* Actions */}
            <OrderActions
              currentStatus={orderData.order.status}
              isUpdating={statusMutation.isPending}
              onStatusChange={handleStatusChange}
              onPrintDeliverySlip={handlePrintDeliverySlip}
              onInitiateRefund={handleInitiateRefund}
            />

            {/* Items table */}
            <OrderItemsTable items={orderData.items} />

            {/* Two-column layout on desktop: timeline + notes */}
            <div className="grid gap-6 lg:grid-cols-2">
              <OrderTimeline
                orderId={orderData.order.id}
                payments={orderData.payments}
                isLoading={isNotesLoading}
              />
              <OrderNotes
                notes={notes as OrderNote[]}
                isSubmitting={noteMutation.isPending}
                onAddNote={handleAddNote}
              />
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
